import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, AuthTokens, LoginCredentials, RegisterData } from '../types/auth';
import authService from '../services/authService';

interface TwoFactorChallenge {
  sessionId: string;
}

interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  twoFactorChallenge: TwoFactorChallenge | null;
  login: (credentials: LoginCredentials) => Promise<{ requires_2fa: boolean } | void>;
  verifyTwoFactor: (totpToken: string) => Promise<void>;
  verifyBackupCode: (backupCode: string) => Promise<void>;
  cancelTwoFactor: () => void;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  refreshTokens: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge | null>(null);

  const isAuthenticated = !!user && !!tokens;

  useEffect(() => {
    initializeAuth();
  }, []);

  // Ref, damit der Intervall immer die aktuelle refreshTokens-Funktion nutzt
  const refreshTokensRef = useRef<() => Promise<void>>(null as unknown as () => Promise<void>);

  // Auto-Refresh: Token alle 20 Minuten erneuern, damit nie ein 401 kommt
  useEffect(() => {
    if (!tokens) return;
    const interval = setInterval(() => {
      refreshTokensRef.current?.().catch(() => {});
    }, 20 * 60 * 1000); // 20 Minuten
    return () => clearInterval(interval);
  }, [tokens]);

  const initializeAuth = async () => {
    try {
      const storedTokens = authService.getStoredTokens();
      if (storedTokens) {
        if (authService.isTokenExpired(storedTokens.access_token)) {
          await refreshTokens();
        } else {
          const userData = await authService.getCurrentUser(storedTokens.access_token);
          setUser(userData);
          setTokens(storedTokens);
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      authService.removeTokens();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);

    if (response.requires_2fa && response.session_id) {
      // 2FA required - store session, don't issue tokens yet
      setTwoFactorChallenge({ sessionId: response.session_id });
      return { requires_2fa: true };
    }

    // No 2FA or trusted device - complete login
    if (response.access_token && response.refresh_token) {
      const authTokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_type: response.token_type,
      };
      authService.storeTokens(authTokens);
      setTokens(authTokens);

      const userData = await authService.getCurrentUser(authTokens.access_token);
      setUser(userData);
    }
  };

  const verifyTwoFactor = async (totpToken: string) => {
    if (!twoFactorChallenge) {
      throw new Error('No 2FA challenge active');
    }

    const response = await authService.verifyTwoFactorLogin(
      twoFactorChallenge.sessionId,
      totpToken,
    );

    if (response.access_token && response.refresh_token) {
      const authTokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_type: response.token_type,
      };
      authService.storeTokens(authTokens);
      setTokens(authTokens);
      setTwoFactorChallenge(null);

      const userData = await authService.getCurrentUser(authTokens.access_token);
      setUser(userData);
    }
  };

  const verifyBackupCode = async (backupCode: string) => {
    if (!twoFactorChallenge) {
      throw new Error('No 2FA challenge active');
    }

    const response = await authService.verifyBackupCode(
      twoFactorChallenge.sessionId,
      backupCode,
    );

    if (response.access_token && response.refresh_token) {
      const authTokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_type: response.token_type,
      };
      authService.storeTokens(authTokens);
      setTokens(authTokens);
      setTwoFactorChallenge(null);

      const userData = await authService.getCurrentUser(authTokens.access_token);
      setUser(userData);
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorChallenge(null);
  };

  const register = async (userData: RegisterData) => {
    const authTokens = await authService.register(userData);
    authService.storeTokens(authTokens);
    setTokens(authTokens);

    const userInfo = await authService.getCurrentUser(authTokens.access_token);
    setUser(userInfo);
  };

  const logout = () => {
    authService.removeTokens();
    setUser(null);
    setTokens(null);
    setTwoFactorChallenge(null);
  };

  const refreshTokens = async () => {
    try {
      const storedTokens = authService.getStoredTokens();
      if (storedTokens?.refresh_token) {
        const newTokens = await authService.refreshToken(storedTokens.refresh_token);
        authService.storeTokens(newTokens);
        setTokens(newTokens);

        const userData = await authService.getCurrentUser(newTokens.access_token);
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
      logout();
      throw error;
    }
  };

  // Ref aktualisieren, damit der Intervall immer die aktuelle Funktion nutzt
  refreshTokensRef.current = refreshTokens;

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    twoFactorChallenge,
    login,
    verifyTwoFactor,
    verifyBackupCode,
    cancelTwoFactor,
    register,
    logout,
    refreshTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
